import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
interface CaseStudy {
  title: string;
  slug: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
}
const caseStudies: CaseStudy[] = [{
  title: 'Case Western Reserve University',
  slug: 'case-western',
  description: 'Designed AI-powered faculty system achieving 85% accuracy and securing Amazon pilot program for 2026 rollout.',
  image: 'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
  tags: ['AI Innovation', '0-to-1', 'Human Systems'],
  category: 'AI EXPLORATION'
}, {
  title: 'US Bank Commercial Card',
  slug: 'us-bank',
  description: 'Transformed 54-day manual process into digital platform, achieving 98% satisfaction and 96% adoption.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
  tags: ['Digital Transformation', 'B2B', 'Growth'],
  category: 'FINANCIAL SERVICES'
}, {
  title: 'Dialexa: Unisys',
  slug: 'cyber-security',
  description: 'Redesigned enterprise security platform to win sales meetings and position Unisys competitively in cybersecurity market.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
  tags: ['Enterprise Software', 'B2B', 'SaaS'],
  category: 'DIGITAL PROTECTION'
}, {
  title: 'Watson vs Dialogflow',
  slug: 'ai-comparison',
  description: 'Evaluated conversational AI platforms and built working demos that secured funding for enterprise call center automation.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
  tags: ['AI Strategy', 'Enterprise Logic', 'Conversational Design'],
  category: 'AI COMPARISON'
}];
export function ParallaxCaseStudies() {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const handleImageClick = (slug: string) => {
    // Prevent navigation for Watson vs Dialogflow (coming soon)
    if (slug === 'ai-comparison') {
      return;
    }
    // CRITICAL: Set flag that user is navigating from home page
    sessionStorage.setItem('navigatedFromHome', 'true');
    sessionStorage.removeItem('cameFromChat'); // Clear chat flag if it exists
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    navigate(`/case-study/${slug}`);
  };
  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    // Don't change cursor for Watson vs Dialogflow
    if (caseStudies[index].slug === 'ai-comparison') {
      if (window.cursorDefault) {
        window.cursorDefault();
      }
      return;
    }
    if (window.cursorSelectProject) {
      window.cursorSelectProject();
    }
  };
  const handleMouseLeave = () => {
    setHoveredIndex(null);
    if (window.cursorDefault) {
      window.cursorDefault();
    }
  };
  return <section className="relative w-full bg-black" id="projects" data-id="element-1997">
      {/* Sticky Header Container */}
      <div className="relative" data-id="element-1998">
        {/* Sticky Header */}
        <div className="sticky top-0 z-[100] bg-white w-full flex items-center justify-center h-[80px] shadow-[0_2px_4px_rgba(0,0,0,0.05)]" data-id="element-1999">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black uppercase leading-tight" style={{
          fontFamily: "'Gefika', sans-serif",
          fontWeight: 400,
          letterSpacing: '0.02em'
        }} data-id="element-2000">
            Case Studies
          </h2>
        </div>

        {/* Case Study Stack Container */}
        <div className="relative w-full" data-id="element-2001">
          {caseStudies.map((study, index) => {
          const isComingSoon = study.slug === 'ai-comparison';
          return <div key={study.slug} className="relative w-full h-screen overflow-hidden flex items-center" onClick={() => handleImageClick(study.slug)} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} style={{
            cursor: isComingSoon ? 'default' : 'pointer'
          }} data-id="element-2002">
                {/* Background Image */}
                <div className="absolute inset-0 w-full h-full" data-id="element-2003">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-700 ease-out" style={{
                objectPosition: study.slug === 'us-bank' ? 'top' : 'center',
                transform: hoveredIndex === index && !isComingSoon ? 'scale(1.05)' : 'scale(1)'
              }} data-id="element-2004" />

                  {/* FIXED: Darker gradient overlay on mobile for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent md:from-black/90 md:via-black/50" data-id="element-2005"></div>
                  <div className="absolute inset-0 bg-black/30 md:bg-black/20" data-id="element-2006"></div>
                </div>

                {/* Content Container - Left Aligned */}
                <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 sm:px-12 md:px-16 lg:px-24 h-full flex flex-col justify-center items-start pointer-events-none" data-id="element-2007">
                  <div className="max-w-xl lg:max-w-2xl" data-id="element-2008">
                    {/* Category */}
                    <p className="text-xs sm:text-sm md:text-sm lg:text-base font-medium uppercase mb-4 text-white md:text-white/60 tracking-[0.2em] reveal" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-2009">
                      {study.category}
                    </p>

                    {/* Title */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-6 text-left reveal" style={{
                  fontFamily: "'Gefika', sans-serif",
                  fontWeight: 400
                }} data-id="element-2010">
                      {study.slug === 'us-bank' ? <>
                          US Bank
                          <br data-id="element-2011" />
                          Commercial Card
                        </> : study.slug === 'case-western' ? <>
                          Case Western
                          <br data-id="element-2012" />
                          Reserve University
                        </> : study.title}
                    </h3>

                    {/* Tags - FIXED: Better contrast on mobile */}
                    <div className="flex flex-wrap justify-start gap-2 mb-8 reveal lg:gap-3 lg:mb-10" data-id="element-2013">
                      {study.tags.map((tag, i) => <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-[10px] sm:text-xs md:text-xs lg:text-sm text-white uppercase tracking-wider md:bg-white/5 md:border-white/10 lg:px-4 lg:py-1.5" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2014">
                          {tag}
                        </span>)}
                    </div>

                    {/* Description - FIXED: 100% white on mobile, softer on desktop */}
                    <p className="text-base sm:text-lg md:text-lg lg:text-xl text-white leading-relaxed max-w-lg md:max-w-lg lg:max-w-xl text-left mb-10 lg:mb-12 reveal" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-2015">
                      {study.description}
                    </p>

                    {/* Button - Coming Soon or View Case Study */}
                    {isComingSoon ? <div className="flex items-center gap-3 text-white/40 pointer-events-none lg:mt-12" data-id="element-2016">
                        <span className="text-xs sm:text-sm uppercase tracking-[0.15em] border-b border-white/20 pb-1 lg:text-base" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2017">
                          coming soon
                        </span>
                      </div> : <button onClick={e => {
                  e.stopPropagation();
                  handleImageClick(study.slug);
                }} className="group flex items-center gap-3 text-white pointer-events-auto lg:mt-12" data-cursor-shrink-target="true" onMouseEnter={() => window.cursorDefault && window.cursorDefault()} onMouseLeave={() => hoveredIndex === index && window.cursorSelectProject && window.cursorSelectProject()} data-id="element-2018">
                        <span className="text-xs sm:text-sm uppercase tracking-[0.15em] border-b border-white/30 pb-1 group-hover:border-white transition-colors duration-300 lg:text-base" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-2019">
                          view case study
                        </span>
                        <ArrowRightIcon className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 lg:w-5 lg:h-5" data-id="element-2020" />
                      </button>}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}